import React, { useEffect, useState } from "react";
import {
  IAddonRequirementInput,
  IAddressLocation,
  IBookingCreateInput,
  IDriverAvailableSlot,
} from "../../../../reducers/bookings/types";
import { formatGraphQLErrorMessage, getAddonIcon } from "../../../utils";
import styles from "./index.module.css";
import {
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
} from "@material-ui/core";
import {
  GET_DRIVER_AVAILABLE_SLOTS,
  GET_VALET_ADDON_PRICE,
} from "../../../../graphql/addOns/getValetAddOn";
import PlacesAutocomplete, {
  geocodeByAddress,
} from "react-places-autocomplete";
import { Field, Form, Formik } from "formik";
import _ from "lodash";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { useLazyQuery } from "@apollo/client";
import { ValetType, IAddonType, LocationType, valetTypes, ISlot, IDriverSlot, IValetSlotData, IDriverAvailabilitySlot } from "./types";
import { ApolloError } from "apollo-boost";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { DateTime as d, DateTime } from "luxon";
import { useTranslation } from "react-i18next";

interface IProps {
  addon: IAddonType;
  bookingData: IBookingCreateInput;
  selectedAddon?: IAddonRequirementInput;
  onSelect(args: {
    addonType: any;
    quantity: number;
    rate?: number;
    pickupOtherLocation?: IAddressLocation;
    dropoffOtherLocation?: IAddressLocation;
    valetType?: String;
    isNonNegotiable?: boolean;
    valetSlot?: IValetSlotData;
  }): void;
}

const addressLocation: IAddressLocation = {
  city: "",
  country: "",
  fullAddress: "",
  state: "",
  street: "",
  zipcode: "",
  location: {
    type: "Point",
    latitude: 0,
    longitude: 0,
  },
};

const active = {
  backgroundColor: "#fafafa",
  cursor: "pointer",
} as React.CSSProperties;

const inactive = {
  backgroundColor: "#ffffff",
  cursor: "pointer",
} as React.CSSProperties;

const divStyle = {
  border: "1px dashed rgba(255, 199, 0, 1)",
  backgroundColor: "#f2f1e8",
};

const valetIconStyle = {
  marginBottom: 20,
  display: "flex",
  justifyContent: "center",
};

const valetIcon = {
  width: 30,
  height: 30,
};

export const ValetCard: React.FC<IProps> = (props) => {
  const { addon, selectedAddon, onSelect, bookingData } = props;
  const website = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );
  const currentBranchLocations = website.branches.find(branch => branch.id === props.bookingData.branchId)?.locations
  const snackbar = useSnackBar();
  const [selected, setSelected] = useState<boolean>(!!selectedAddon?.addon);
  const [pickupOtherLocation, setPickupOtherLocation] =
    useState<IAddressLocation>(_.cloneDeep(addressLocation));
  const [dropoffOtherLocation, setDropoffOtherLocation] =
    useState<IAddressLocation>(_.cloneDeep(addressLocation));
  const [selectedAddressType, setSelectedAddressType] = useState<string>(
    ValetType.DELIVERY_AND_COLLECTION
  );
  const [deliveryAddonRate, setDeliveryAddonRate] = useState<number>(0);
  const [collectionAddonRate, setCollectionAddonRate] = useState<number>(0);
  const [addonRate, setAddonRate] = useState<number>(0);
  const [selectedAddressTypeKey, setSelectedAddressTypeKey] =
    useState<LocationType>();
  const [valetPickupLocation, setValetPickupLocation] = useState<any>();
  const [valetDropOffLocation, setValetDropOffLocation] = useState<any>();
  const [pickupBranchId, setPickupBranchId] = useState<string | undefined>();
  const [dropOffBranchId, setDropOffBranchId] = useState<string | undefined>();
  const [driverDeliverySlots, setDriverDeliverySlots] = useState<IDriverAvailabilitySlot[]>([]);
  const [driverCollectionSlots, setDriverCollectionSlots] = useState<IDriverAvailabilitySlot[]>([]);
  const [selectedDeliverySlot, setSelectedDeliverySlot] = useState<IDriverAvailabilitySlot>();
  const [selectedCollectionSlot, setSelectedCollectionSlot] = useState<IDriverAvailabilitySlot>();
  const [selectedDeliveryDriver, setSelectedDeliveryDriver] = useState<IDriverSlot>();
  const [selectedCollectionDriver, setSelectedCollectionDriver] = useState<IDriverSlot>();
  const [valetSlot, setValetSlot] = useState<IValetSlotData>(props.bookingData.valetSlot)
  const [selectedDeliveryDriverId, setSelectedDeliveryDriverId] = useState<string>("");
  const [selectedCollectionDriverId, setSelectedCollectionDriverId] = useState<string>("");

  const logisticsEnabled = useSelector(
    (state: IAppState) =>
      state.consumerWebsiteReducer.consumerWebsite.tenancy.logisticsEnabled
  );
  const { t } = useTranslation();
  const [isNonNegotiable, setIsNonNegotiable] = useState<boolean>(props.bookingData.isNonNegotiable || false);

  const [getPickupSlots, { loading: loadingDeliverySlots }] = useLazyQuery(GET_DRIVER_AVAILABLE_SLOTS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setDriverDeliverySlots(data.consumerGetAvailableDriverSlots?.slots ?? []); // pickupOtherLocation -> DELIVERY
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      });
    }
  });

  const [getDropoffSlots, { loading: loadingCollectionSlots }] = useLazyQuery(GET_DRIVER_AVAILABLE_SLOTS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setDriverCollectionSlots(data.consumerGetAvailableDriverSlots?.slots ?? []); // dropoffOtherLocation -> COLLECTION
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR,
      });
    }
  });

  const [getValetAddonPrice, { data: valetAddonPriceData }] = useLazyQuery(GET_VALET_ADDON_PRICE, {
    fetchPolicy: "network-only",
    onCompleted: (valetAddonPriceData: any) => {
      if (selectedAddressType && valetAddonPriceData && valetAddonPriceData.consumerValetAddonPrice >= 0) {
        if (pickupOtherLocation.fullAddress && selectedAddressTypeKey === LocationType.PICKUP_LOCATION) {
          setDeliveryAddonRate(valetAddonPriceData.consumerValetAddonPrice)
        }
        if (dropoffOtherLocation.fullAddress && selectedAddressTypeKey === LocationType.DROP_OFF_LOCATION) {
          setCollectionAddonRate(valetAddonPriceData.consumerValetAddonPrice)
        }
        if (selectedAddressType === ValetType.DELIVERY && pickupOtherLocation.fullAddress) {
          setSelected(true)
          setAddonRate(valetAddonPriceData.consumerValetAddonPrice)
          onSelect({
            addonType: addon,
            quantity: 1,
            rate: valetAddonPriceData.consumerValetAddonPrice,
            pickupOtherLocation,
            dropoffOtherLocation,
            valetSlot,
            isNonNegotiable
          });
        }
        if (selectedAddressType === ValetType.COLLECTION && dropoffOtherLocation.fullAddress) {
          setSelected(true)
          setAddonRate(valetAddonPriceData.consumerValetAddonPrice)
          onSelect({
            addonType: addon,
            quantity: 1,
            rate: valetAddonPriceData.consumerValetAddonPrice,
            pickupOtherLocation,
            dropoffOtherLocation,
            valetSlot,
            isNonNegotiable
          });
        }
      }
    },
    onError: (error: ApolloError) => {
      setPickupOtherLocation(_.cloneDeep(addressLocation));
      setDropoffOtherLocation(_.cloneDeep(addressLocation));
      setSelected(false);
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  useEffect(() => {
    if (!selected) {
      props.onSelect({ addonType: addon, quantity: 0 });
    }
  }, [selected]);

  useEffect(() => {
    if (selectedAddon && bookingData) {
      const currentServiceLocation = currentBranchLocations?.find(
        (location) => location.id === bookingData.pickupServiceLocation
      );
      if (bookingData?.valetSlot?.deliverySlot && !bookingData?.valetSlot?.collectionSlot) {
        setSelectedDeliverySlot({
          startTime: bookingData?.valetSlot?.deliverySlot?.slot || "",
          endTime: "",
          driverId: bookingData?.valetSlot?.deliverySlot?.driverId || "",
        })
      }
      if (bookingData?.valetSlot?.collectionSlot && !bookingData?.valetSlot?.deliverySlot) {
        setSelectedCollectionSlot({
          startTime: bookingData?.valetSlot?.collectionSlot?.slot || "",
          endTime: "",
          driverId: bookingData?.valetSlot?.collectionSlot?.driverId || "",
        })
      }
      if (bookingData?.valetSlot?.deliverySlot && bookingData?.valetSlot?.collectionSlot) {
        setSelectedDeliverySlot({
          startTime: bookingData?.valetSlot?.deliverySlot?.slot || "",
          endTime: "",
          driverId: bookingData?.valetSlot?.deliverySlot?.driverId || "",
        })
        setSelectedCollectionSlot({
          startTime: bookingData?.valetSlot?.collectionSlot?.slot || "",
          endTime: "",
          driverId: bookingData?.valetSlot?.collectionSlot?.driverId || "",
        })
      }
      if (bookingData.pickupOtherLocation?.fullAddress && !bookingData.dropoffOtherLocation?.fullAddress) {
        setPickupOtherLocation(bookingData.pickupOtherLocation)
        setDeliveryAddonRate(selectedAddon.rate)
        setAddonRate(selectedAddon.rate)
        setSelectedAddressType(ValetType.DELIVERY)
        if (website?.organisation?.supplierPortalEnabled) {
          getPickupSlots({
            variables: {
              driverAvailableSlotArgs: {
                bookingCategory: bookingData.bookingCategory,
                jobDate: bookingData.pickupDateTime,
                pickupCoordinate: {
                  lat: currentServiceLocation?.address.geometry?.latitude,
                  lng: currentServiceLocation?.address.geometry?.longitude,
                },
                dropOffCoordinate: {
                  lat: bookingData?.pickupOtherLocation?.location?.latitude,
                  lng: bookingData?.pickupOtherLocation?.location?.longitude,
                },
              },
              branchId: bookingData.branchId
            },
          })
        }
      }
      if (bookingData.dropoffOtherLocation?.fullAddress && !bookingData.pickupOtherLocation?.fullAddress) {
        setDropoffOtherLocation(bookingData.dropoffOtherLocation)
        setCollectionAddonRate(selectedAddon.rate)
        setAddonRate(selectedAddon.rate)
        setSelectedAddressType(ValetType.COLLECTION)
        if (website?.organisation?.supplierPortalEnabled) {
          getDropoffSlots({
            variables: {
              driverAvailableSlotArgs: {
                bookingCategory: bookingData.bookingCategory,
                jobDate: bookingData.dropoffDateTime,
                pickupCoordinate: {
                  lat: bookingData?.dropoffOtherLocation?.location?.latitude,
                  lng: bookingData?.dropoffOtherLocation?.location?.longitude,
                },
                dropOffCoordinate: {
                  lat: currentServiceLocation?.address.geometry?.latitude,
                  lng: currentServiceLocation?.address.geometry?.longitude,
                },
              },
              branchId: bookingData.branchId
            },
          })
        }
      }

      if (bookingData.pickupOtherLocation?.fullAddress && bookingData.dropoffOtherLocation?.fullAddress) {
        setPickupOtherLocation(bookingData.pickupOtherLocation);
        setDropoffOtherLocation(bookingData.dropoffOtherLocation);
        setAddonRate(selectedAddon.rate);
        setSelectedAddressType(ValetType.DELIVERY_AND_COLLECTION);

        // Pickup slots
        setSelectedAddressTypeKey(LocationType.PICKUP_LOCATION);
        if (website?.organisation?.supplierPortalEnabled) {
          getPickupSlots({
            variables: {
              driverAvailableSlotArgs: {
                bookingCategory: bookingData.bookingCategory,
                jobDate: bookingData.pickupDateTime,
                pickupCoordinate: {
                  lat: currentServiceLocation?.address?.geometry?.latitude,
                  lng: currentServiceLocation?.address?.geometry?.longitude,
                },
                dropOffCoordinate: {
                  lat: bookingData?.pickupOtherLocation?.location?.latitude,
                  lng: bookingData?.pickupOtherLocation?.location?.longitude,
                },
              },
              branchId: bookingData.branchId
            },
          });
        }

        // Dropoff slots
        setSelectedAddressTypeKey(LocationType.DROP_OFF_LOCATION);
        if (website?.organisation?.supplierPortalEnabled) {
          getDropoffSlots({
            variables: {
              driverAvailableSlotArgs: {
                bookingCategory: bookingData.bookingCategory,
                jobDate: bookingData.dropoffDateTime,
                pickupCoordinate: {
                  lat: bookingData?.dropoffOtherLocation?.location?.latitude,
                  lng: bookingData?.dropoffOtherLocation?.location?.longitude,
                },
                dropOffCoordinate: {
                  lat: currentServiceLocation?.address?.geometry?.latitude,
                  lng: currentServiceLocation?.address?.geometry?.longitude,
                },
              },
              branchId: bookingData.branchId
            },
          });
        }
      }

    }
  }, [selectedAddon, bookingData]);

  useEffect(() => {
    if (
      pickupOtherLocation.fullAddress &&
      pickupOtherLocation.city &&
      dropoffOtherLocation.fullAddress &&
      dropoffOtherLocation.city
    ) {
      if (selectedAddressType === ValetType.DELIVERY_AND_COLLECTION) {
        setSelected(true);
        setAddonRate(deliveryAddonRate + collectionAddonRate);
        onSelect({
          addonType: addon,
          quantity: 1,
          rate: deliveryAddonRate + collectionAddonRate || addonRate,
          pickupOtherLocation,
          dropoffOtherLocation,
          valetSlot,
          isNonNegotiable
        });
      }
    }
  }, [
    pickupOtherLocation,
    dropoffOtherLocation,
    deliveryAddonRate,
    collectionAddonRate
  ]);

  useEffect(() => {
    if (
      bookingData &&
      (bookingData.pickupServiceLocation || bookingData.dropoffServiceLocation)
    ) {
      if (bookingData.pickupServiceLocation) {
        for (let i = 0; i < website.branches.length; i++) {
          const pickup = website.branches[i].locations.find(
            (location) => location.id === bookingData.pickupServiceLocation
          );
          if (pickup) {
            if (typeof pickup?.branch !== "string") {
              setPickupBranchId(pickup?.branch?.id);
            }
            setValetPickupLocation(pickup.address);
          }
        }
      }

      if (bookingData.dropoffServiceLocation) {
        for (let i = 0; i < website.branches.length; i++) {
          const dropOff = website.branches[i].locations.find(
            (location) => location.id === bookingData.dropoffServiceLocation
          );
          if (dropOff) {
            if (typeof dropOff?.branch !== "string") {
              setDropOffBranchId(dropOff?.branch?.id);
            }
            setValetDropOffLocation(dropOff.address);
          }
        }
      }
    }
  }, [
    website.branches,
    bookingData.pickupServiceLocation,
    bookingData.dropoffServiceLocation,
  ]);

  const handleSelectAddress = (address: string, type: LocationType) => {
    geocodeByAddress(address)
      .then((results) => {
        if (results.length) {
          const loc = _.cloneDeep(addressLocation);
          const place = results[0];
          const streetName: string[] = [];
          place.address_components.forEach((item) => {
            if (item.types.includes("country")) {
              loc.country = item.long_name;
            } else if (item.types.includes("postal_code")) {
              loc.zipcode = item.long_name;
            } else if (item.types.includes("administrative_area_level_2")) {
              loc.state = item.short_name;
            } else if (
              item.types.includes("postal_town") ||
              item.types.includes("locality")
            ) {
              loc.city = item.long_name;
            } else if (
              item.types.includes("street_number") ||
              item.types.includes("route")
            ) {
              streetName.push(item.long_name);
            }
          });
          loc.street = streetName.join(", ");
          loc.fullAddress = address;
          const lng = place.geometry.location.lng();
          const lat = place.geometry.location.lat();
          loc.location = {
            type: "Point",
            latitude: lat,
            longitude: lng,
          };

          if (
            loc &&
            loc.location &&
            loc.location.latitude &&
            loc.location.longitude
          ) {
            const currentServiceLocation = currentBranchLocations?.find(
              (location) => location.id === bookingData.pickupServiceLocation
            );
            if (
              currentServiceLocation &&
              currentServiceLocation.address &&
              currentServiceLocation.address.geometry
            ) {
              if (type === LocationType.PICKUP_LOCATION) {
                setSelectedAddressTypeKey(LocationType.PICKUP_LOCATION);
                getValetAddonPrice({
                  variables: {
                    branchId: pickupBranchId ? pickupBranchId : "",
                    startLat: String(
                      currentServiceLocation.address.geometry?.latitude
                    ),
                    startLong: String(
                      currentServiceLocation.address.geometry?.longitude
                    ),
                    endLat: String(loc.location.latitude),
                    endLong: String(loc.location.longitude)
                  }
                });
                setPickupOtherLocation({
                  ...pickupOtherLocation,
                  ...loc
                });
              }
              if (type === LocationType.DROP_OFF_LOCATION) {
                setSelectedAddressTypeKey(LocationType.DROP_OFF_LOCATION);
                getValetAddonPrice({
                  variables: {
                    branchId: pickupBranchId ? pickupBranchId : "",
                    startLat: String(
                      currentServiceLocation.address.geometry?.latitude
                    ),
                    startLong: String(
                      currentServiceLocation.address.geometry?.longitude
                    ),
                    endLat: String(loc.location.latitude),
                    endLong: String(loc.location.longitude)
                  }
                });
                setDropoffOtherLocation({
                  ...dropoffOtherLocation,
                  ...loc
                });
              }
            }
          }
        }
      })
      .catch((error) => console.error("Error", error));
  };

  const handleOnChange = (value: string, type: LocationType) => {
    if (type === LocationType.PICKUP_LOCATION) {
      if (!value) {
        setPickupOtherLocation(_.cloneDeep(addressLocation));
        setDeliveryAddonRate(0);
        setAddonRate(0);
        setSelected(false);
      } else {
        setPickupOtherLocation({
          ...pickupOtherLocation,
          fullAddress: value,
        });
      }
    }
    if (type === LocationType.DROP_OFF_LOCATION) {
      if (!value) {
        setDropoffOtherLocation(_.cloneDeep(addressLocation));
        setCollectionAddonRate(0);
        setAddonRate(0);
        setSelected(false);
      } else {
        setDropoffOtherLocation({
          ...dropoffOtherLocation,
          fullAddress: value,
        });
      }
    }
  };

  const removeValetAddon = () => {
    setPickupOtherLocation(_.cloneDeep(addressLocation));
    setDropoffOtherLocation(_.cloneDeep(addressLocation));
    setSelected(false);
    setDeliveryAddonRate(0);
    setCollectionAddonRate(0);
    setAddonRate(0);
    setIsNonNegotiable(false);
  };

  const getUniqueSortedSlots = (data: IDriverSlot[]): ISlot[] => {
    const parseTime = (timeStr: string): DateTime =>
      d.fromFormat(timeStr.trim().toUpperCase(), 'hh:mm a');

    const formatTime = (dt: DateTime): string =>
      dt.toFormat('hh:mm a');

    const slotMap = new Map<number, ISlot>();

    data.forEach(driver => {
      driver.slots.forEach(slot => {
        const startDt = parseTime(slot.startTime);
        const key = startDt.hour * 60 + startDt.minute;

        if (!slotMap.has(key)) {
          const endDt = parseTime(slot.endTime);
          slotMap.set(key, {
            startTime: formatTime(startDt),
            endTime: formatTime(endDt),
          });
        }
      });
    });

    return Array.from(slotMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([_, slot]) => slot);
  };

  const getRandomDriverForSlot = (
    data: IDriverSlot[],
    selectedSlot: ISlot
  ): IDriverSlot | undefined => {
    const parseTime = (timeStr: string): DateTime =>
      d.fromFormat(timeStr.trim().toUpperCase(), 'hh:mm a');

    const targetStart = parseTime(selectedSlot.startTime);

    const matchingDrivers = data.filter(driver =>
      driver.slots.some(slot =>
        parseTime(slot.startTime).equals(targetStart)
      )
    );

    if (matchingDrivers.length === 0) return undefined;

    const randomIndex = Math.floor(Math.random() * matchingDrivers.length);
    return matchingDrivers[randomIndex];
  };

  const isSlotDisabled = (
    slot: ISlot,
    bookingStart: string,
    type: "delivery" | "collection" = "delivery"
  ): boolean => {
    const toMinutes = (timeStr: string): number => {
      const dt = d.fromFormat(timeStr.trim().toUpperCase(), "hh:mm a");
      return dt.hour * 60 + dt.minute;
    };

    const slotMinutes = toMinutes(slot.startTime);
    const pickupDt = d.fromISO(bookingData.pickupDateTime);
    const dropoffDt = d.fromISO(bookingData.dropoffDateTime || "");
    const pickupMinutes = pickupDt.hour * 60 + pickupDt.minute;
    const dropoffMinutes = dropoffDt.hour * 60 + dropoffDt.minute;
    const isSameDay = pickupDt.hasSame(dropoffDt, "day");

    if (type === "delivery") {
      if (slotMinutes < pickupMinutes) return true;
      if (isSameDay && slotMinutes > dropoffMinutes) return true;
      return false;
    }

    if (type === "collection") {
      if (isSameDay) {
        if (selectedDeliverySlot?.startTime) {
          const deliveryMinutes = toMinutes(selectedDeliverySlot.startTime);
          return slotMinutes <= deliveryMinutes;
        }
        return slotMinutes < pickupMinutes;
      }
      return false;
    }

    return false;
  };

  const handleSlotSelection = (slot: IDriverAvailabilitySlot, type: "delivery" | "collection") => {
    const isDelivery = type === "delivery";

    const deliverySlotTime = isDelivery
      ? slot.startTime
      : selectedDeliverySlot?.startTime || "";

    const collectionSlotTime = !isDelivery
      ? slot.startTime
      : selectedCollectionSlot?.startTime || "";

    const deliveryDriverId = isDelivery
      ? slot.driverId
      : selectedDeliveryDriverId;

    const collectionDriverId = !isDelivery
      ? slot.driverId
      : selectedCollectionDriverId;

    if (isDelivery) {
      setSelectedDeliverySlot(slot);
      setSelectedDeliveryDriverId(slot.driverId);
    } else {
      setSelectedCollectionSlot(slot);
      setSelectedCollectionDriverId(slot.driverId);
    }

    let valetSlot: IValetSlotData;

    switch (selectedAddressType) {
      case ValetType.DELIVERY:
        valetSlot = {
          deliverySlot: { slot: deliverySlotTime, driverId: deliveryDriverId },
          collectionSlot: { slot: "", driverId: "" },
        };
        break;

      case ValetType.COLLECTION:
        valetSlot = {
          deliverySlot: { slot: "", driverId: "" },
          collectionSlot: { slot: collectionSlotTime, driverId: collectionDriverId },
        };
        break;

      case ValetType.DELIVERY_AND_COLLECTION:
        valetSlot = {
          deliverySlot: { slot: deliverySlotTime, driverId: deliveryDriverId },
          collectionSlot: { slot: collectionSlotTime, driverId: collectionDriverId },
        };
        break;

      default:
        return;
    }
    setValetSlot(valetSlot);
    onSelect({
      addonType: addon,
      quantity: 1,
      rate: addonRate,
      pickupOtherLocation,
      dropoffOtherLocation,
      valetType: selectedAddressType,
      valetSlot,
      isNonNegotiable
    });
  };

  return (
    <div
      className={styles.customAddonCard}
      style={selected ? divStyle : { width: "100%" }}
    >
      <div style={valetIconStyle}>
        <img src={getAddonIcon("VALET")} alt="custom addon" style={valetIcon} />
      </div>
      <div className={styles.addonInfo}>
        <p>{addon.displayName ? addon.displayName : addon.name}</p>
      </div>
      <div></div>
      <div style={{ marginTop: 20, padding: 10, width: "100%" }}>
        <Grid container item xs={8} md={12} xl={12} sm={12}>
          <Formik enableReinitialize initialValues={{}} onSubmit={() => { }}>
            {(props) => (
              <Form style={{ width: "100%" }}>
                <Grid container item md={8} style={{ margin: "auto" }}>
                  <FormControl variant="outlined" fullWidth>
                    <Field
                      component={TextField}
                      name={"typeOfValet"}
                      fullWidth
                      select
                      label={t("root:typeOfValet")}
                      value={selectedAddressType}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        onChange: (
                          event: React.ChangeEvent<{ value: unknown }>
                        ) => {
                          setPickupOtherLocation(_.cloneDeep(addressLocation));
                          setDropoffOtherLocation(_.cloneDeep(addressLocation));
                          setSelected(false);
                          setDeliveryAddonRate(0);
                          setCollectionAddonRate(0);
                          setAddonRate(0);
                          setSelectedAddressType(event.target.value as string);
                        },
                      }}
                      placeholder={t("root:selectValet")}
                    >
                      {valetTypes.map((item: string, index: number) => (
                        <MenuItem key={index} value={item}>
                          {t(`root:usevaletTypes.${item}`)}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  md={12}
                  xl={12}
                  spacing={2}
                  style={{ margin: 0 }}
                >
                  {(selectedAddressType === ValetType.DELIVERY ||
                    selectedAddressType ===
                    ValetType.DELIVERY_AND_COLLECTION) && (
                      <Grid
                        xs={12}
                        sm={12}
                        md={12}
                        xl={12}
                        style={{ marginTop: 20 }}
                      >
                        <PlacesAutocomplete
                          value={
                            pickupOtherLocation
                              ? pickupOtherLocation.fullAddress
                              : ""
                          }
                          onChange={(value: string) => {
                            handleOnChange(value, LocationType.PICKUP_LOCATION);
                          }}
                          onSelect={(value) => {
                            handleSelectAddress(
                              value,
                              LocationType.PICKUP_LOCATION
                            );
                          }}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                          }) => (
                            <div className="location-search">
                              <Field
                                component={TextField}
                                fullWidth
                                style={{
                                  backgroundColor: selectedAddon ? "#FFFFFF" : "",
                                }}
                                label={t("root:deliveryAddress")}
                                {...getInputProps({
                                  className: "location-search-input",
                                  name: "state",
                                  placeholder: t("root:deliveryAddress"),
                                })}
                              ></Field>
                              <div className="autocomplete-dropdown-container">
                                {suggestions.map((suggestion, idx) => {
                                  const style = suggestion.active
                                    ? active
                                    : inactive;
                                  suggestion.id = suggestion.id || idx.toString();
                                  return (
                                    <div
                                      className="suggestions"
                                      {...getSuggestionItemProps(suggestion, {
                                        style,
                                      })}
                                    >
                                      {suggestion.description}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </PlacesAutocomplete>
                      </Grid>
                    )}
                  {(selectedAddressType === ValetType.COLLECTION ||
                    selectedAddressType ===
                    ValetType.DELIVERY_AND_COLLECTION) && (
                      <Grid
                        xs={12}
                        sm={12}
                        md={12}
                        xl={12}
                        style={{ marginTop: 20 }}
                      >
                        <PlacesAutocomplete
                          value={
                            dropoffOtherLocation
                              ? dropoffOtherLocation.fullAddress
                              : ""
                          }
                          onChange={(value: string) => {
                            handleOnChange(value, LocationType.DROP_OFF_LOCATION);
                          }}
                          onSelect={(value) => {
                            handleSelectAddress(
                              value,
                              LocationType.DROP_OFF_LOCATION
                            );
                          }}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                          }) => (
                            <Grid className="location-search">
                              <Field
                                component={TextField}
                                fullWidth
                                style={{
                                  backgroundColor: selectedAddon ? "#FFFFFF" : "",
                                }}
                                label={t("root:collectionAddress")}
                                {...getInputProps({
                                  className: "location-search-input",
                                  name: "state",
                                  placeholder: t("root:collectionAddress"),
                                })}
                              ></Field>
                              <Grid className="autocomplete-dropdown-container">
                                {suggestions.map((suggestion, idx) => {
                                  const style = suggestion.active
                                    ? active
                                    : inactive;
                                  suggestion.id = suggestion.id || idx.toString();
                                  return (
                                    <Grid
                                      className="suggestions"
                                      {...getSuggestionItemProps(suggestion, {
                                        style,
                                      })}
                                    >
                                      {suggestion.description}
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Grid>
                          )}
                        </PlacesAutocomplete>
                      </Grid>
                    )}
                </Grid>
              </Form>
            )}
          </Formik>
        </Grid>
        {logisticsEnabled && selected && (
          <div style={{ marginTop: 20, width: "100%" }}>
            <div
              style={{
                marginBottom: 20,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isNonNegotiable}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setIsNonNegotiable(value);
                      onSelect({
                        addonType: addon,
                        quantity: 1,
                        rate: addonRate,
                        pickupOtherLocation,
                        dropoffOtherLocation,
                        valetSlot,
                        isNonNegotiable: value,
                      });
                    }}
                    color="primary"
                  />
                }
                label={t("root:nonNegotiable")}
              />
            </div>
            {/* Delivery Slots */}
            {
              selected &&
              (selectedAddressType === ValetType.DELIVERY ||
                selectedAddressType === ValetType.DELIVERY_AND_COLLECTION) &&
              pickupOtherLocation.fullAddress && (
                <div style={{ marginBottom: 15 }}>
                  <p style={{ fontWeight: "bold", marginBottom: 10 }}>{t("root:selectDeliverySlot")}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {driverDeliverySlots?.map((slot, index) => (
                      <button
                        key={`delivery-${index}`}
                        onClick={() => handleSlotSelection(slot, "delivery")}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          backgroundColor:
                            selectedDeliverySlot?.startTime === slot.startTime
                              ? "rgba(255, 199, 0, 1)"
                              : "white",
                          cursor: "pointer",
                          minWidth: "80px",
                          fontWeight:
                            selectedDeliverySlot?.startTime === slot.startTime
                              ? "bold"
                              : "normal",
                        }}
                        disabled={isSlotDisabled(slot, bookingData.pickupDateTime, "delivery")}
                      >
                        {slot.startTime}
                      </button>
                    ))}
                  </div>
                  {!driverDeliverySlots?.length && (
                    <p style={{ marginTop: 10, color: "#666", fontSize: "0.9em" }}>
                      {t("root:driverUnavailableNote")}
                    </p>
                  )}
                </div>
              )}

            {/* Collection Slots */}
            {
              selected &&
              (selectedAddressType === ValetType.COLLECTION ||
                selectedAddressType === ValetType.DELIVERY_AND_COLLECTION) &&
              dropoffOtherLocation.fullAddress && (
                <div>
                  <p style={{ fontWeight: "bold", marginBottom: 10 }}>{t("root:selectCollectionSlot")}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {driverCollectionSlots?.map((slot, index) => (
                      <button
                        key={`collection-${index}`}
                        onClick={() => handleSlotSelection(slot, "collection")}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          backgroundColor:
                            selectedCollectionSlot?.startTime === slot.startTime
                              ? "rgba(255, 199, 0, 1)"
                              : "white",
                          cursor: "pointer",
                          minWidth: "80px",
                          fontWeight:
                            selectedCollectionSlot?.startTime === slot.startTime
                              ? "bold"
                              : "normal",
                        }}
                        disabled={isSlotDisabled(slot, bookingData.dropoffDateTime || "", "collection")}
                      >
                        {slot.startTime}
                      </button>
                    ))}
                  </div>
                  {!driverCollectionSlots?.length && (
                    <p style={{ marginTop: 10, color: "#666", fontSize: "0.9em" }}>
                      {t("root:driverUnavailableNote")}
                    </p>
                  )}
                </div>
              )}
          </div>
        )}
        {selected ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <p
              className="valetCard-remove-btn"
              style={{ backgroundColor: "#fddfdc", color: "#e00101" }}
              onClick={() => removeValetAddon()}
            >
              {t("root:remove")}
            </p>
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};
